import React from 'react';
import { Box, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { OrderPageTypography } from '../themedComponents/OrderPageTypography';
import { customerFacingPriceFormatter } from '../utils';
import { usePriceExplanation } from '../hooks/usePriceExplanation';
import { useOrderPage } from '../orderPageContext/useOrderPage';
import { asOrderPageData } from '../core/types';
import { useLanguage } from '../../utils/GeneralUtils';
import { isBookingFeeOn, withBookingFee } from '../core/pricing';

/**
 * The price, next to the control that changes it. The embed iframe grows to its content height and
 * never scrolls, so a sticky summary is impossible — cause and effect have to share a spot in flow.
 */
export const InlinePriceLine = ({ personsOverride }: { personsOverride?: number }) => {
  const { t } = useTranslation();
  const { orderPageState } = useOrderPage();
  const language = useLanguage();
  const { shouldExplain, isFromPrice, total, persons, perPerson, listPrice, savings, nextStep, unitLabel } =
    usePriceExplanation({
      personsOverride,
    });

  // The total earns its place whenever the rate moves with party size, discount or not: under a
  // minimum charge it is the one number that explains why five people cost the same as two.
  if (orderPageState.isWalkIn || (!shouldExplain && !isFromPrice)) {
    return null;
  }

  // withTax omitted, so this always returns a plain string rather than the tax-note element.
  const money = (n: number) => customerFacingPriceFormatter(n, orderPageState.account) as string;
  const unit = unitLabel?.[language];
  const { account } = orderPageState;
  const isFeeOn = isBookingFeeOn(account);
  const displayTotal = withBookingFee(total, account);
  const displayPerPerson = isFeeOn ? Math.round((displayTotal / persons) * 100) / 100 : perPerson;
  const displayListPrice = listPrice !== null ? withBookingFee(listPrice, account) : null;
  const displaySavings = isFeeOn ? Math.max(0, (displayListPrice ?? 0) - displayTotal) : savings;
  const showAsDeposit = !!asOrderPageData(orderPageState.orderPage)?.show_price_as_deposit;

  return (
    <Stack spacing={0.25} mt={1}>
      <Box display="flex" gap={1} alignItems="baseline">
        <OrderPageTypography variant="h6" style={{ opacity: 0.7 }}>
          {showAsDeposit ? t('order_page.deposit') : t('order_page.total')}
        </OrderPageTypography>
        <OrderPageTypography variant="h3" bold>
          {money(displayTotal)}
        </OrderPageTypography>
      </Box>

      {/* One participant pays the total, so a per-person line would just repeat it. There is no
          saving to show at that size either, which leaves the whole row with nothing to say. */}
      {shouldExplain && persons > 1 && (
        <Box display="flex" gap={1} flexWrap="wrap" alignItems="baseline">
          <OrderPageTypography variant="h6" style={{ opacity: 0.7 }}>
            {unit
              ? t('order_page.pricePerUnit', { price: money(displayPerPerson), unit })
              : t('order_page.pricePerParticipant', { price: money(displayPerPerson) })}
          </OrderPageTypography>
          {displaySavings > 0 && displayListPrice !== null && (
            <>
              <OrderPageTypography variant="h6" style={{ opacity: 0.55, textDecoration: 'line-through' }}>
                {money(displayListPrice)}
              </OrderPageTypography>
              <OrderPageTypography variant="h6" color="button.outlined" bold>
                {t('order_page.priceSavings', { amount: money(displaySavings) })}
              </OrderPageTypography>
            </>
          )}
        </Box>
      )}

      {nextStep && (
        <OrderPageTypography variant="h6" style={{ opacity: 0.7 }}>
          {unit
            ? t('order_page.priceNextStepUnit', { price: money(withBookingFee(nextStep.perPerson, account)), unit })
            : t('order_page.priceNextStep', { price: money(withBookingFee(nextStep.perPerson, account)) })}
        </OrderPageTypography>
      )}
    </Stack>
  );
};
